<section class="dashboard">
  <div class="dashboard-main">
    <header class="dashboard-header">
      <div>
        <h1>{{ estimate()?.id || "Estimate" }}</h1>
      </div>
      <div class="header-actions">
        <button class="action-button" (click)="downloadEstimate()">
          ⬇ Download PDF
        </button>
        <button
          class="action-button primary"
          (click)="saveEstimate()"
          [disabled]="saving()"
        >
          {{ saving() ? "Saving..." : "Save Estimate" }}
        </button>
        <a class="action-button" [routerLink]="['/customers', customerId]">
          ← Back
        </a>
      </div>
    </header>

    @if (loading()) {
      <div class="loading-message">Loading estimate...</div>
    } @else if (estimate(); as estimate) {
      <div class="estimate-container" id="printable-estimate">
        <!-- Header Section -->
        <div class="estimate-header">
          <div class="company-info">
            <img
              class="company-logo"
              src="assets/logo.png"
              alt="Do Your Shades logo"
              id="company-logo-img"
            />
            <div class="company-details">
              <h2>
                <span class="big-g">Big G</span>
                <span class="blinds">Blinds</span>
              </h2>
              <p>Georgetown, TX</p>
              <p>U.S.A</p>
            </div>
          </div>
          <div class="estimate-title">
            <h1>ESTIMATE</h1>
            <p>Estimate# {{ estimate.id }} · Rev {{ estimate.version ?? 1 }}</p>
            @if (customer()?.orderNumber; as orderNumber) {
              <p>Order #{{ orderNumber }}</p>
            }
          </div>
        </div>

        <!-- Bill To Section -->
        <div class="billing-section">
          <div class="bill-to">
            <h3>Bill To:</h3>
            <p>
              <strong>{{ customer()?.name ?? "Customer" }}</strong>
            </p>
            <p>{{ customer()?.address || "Address not provided" }}</p>
            <div class="summary-chips">
              <span class="summary-chip">Rooms: {{ totalRooms }}</span>
              <span class="summary-chip">Blinds: {{ totalBlinds }}</span>
            </div>
          </div>
          <div class="dates">
            <div class="date-item">
              <strong>Estimate Date :</strong>
              <p>{{ estimate.createdAt | date: "MMM dd, yyyy" }}</p>
            </div>
            <div class="date-item">
              <strong>Expiry Date :</strong>
              <p>{{ estimateExpiryDate | date: "MMM dd, yyyy" }}</p>
            </div>
          </div>
        </div>

        <!-- Items Table -->
        <div class="table-wrap">
          <table class="estimate-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Item Description</th>
                <th>Type</th>
                <th>Style</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              @for (
                item of estimate.items;
                track item.windowLabel + item.roomName
              ) {
                <tr>
                  <td>{{ $index + 1 }}</td>
                  <td>
                    <div class="item-desc">
                      <div class="item-room">{{ item.roomName }}</div>
                      <div class="item-window">{{ item.windowLabel }}</div>
                    </div>
                  </td>
                  <td>
                    <div class="item-badges">
                      @if (item.controlType === "motorized") {
                        <span class="item-badge badge-motorized">A-OK Matter motorized</span>
                      } @else if (item.controlType === "cordless") {
                        <span class="item-badge badge-cordless">Cordless</span>
                      } @else {
                        <span class="item-badge badge-manual">Manual</span>
                      }
                      @if (item.hasSolarPanel) {
                        <span class="item-badge badge-solar">Solar</span>
                      }
                    </div>
                  </td>
                  <td>
                    @if (item.style) {
                      <span
                        class="estimate-style"
                        [class.type-semi]="item.type === 'semi-blackout'"
                        [class.type-blackout]="item.type === 'blackout'"
                      >{{ item.style }}</span>
                    } @else {
                      <span class="muted">—</span>
                    }
                  </td>
                  <td>
                    <input
                      type="number"
                      [(ngModel)]="item.rate"
                      (change)="updatePrice($index, item.rate)"
                      class="table-input"
                      step="0.01"
                    />
                  </td>
                  <td>{{ item.amount | currency }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <!-- Totals Section -->
        <div class="totals-section">
          <div class="totals-grid">
            <div class="total-row">
              <span>Sub Total</span>
              <span>{{ estimate.subtotal | currency }}</span>
            </div>
            <div class="total-row highlight">
              <span class="discount-label">
                <input
                  type="text"
                  [(ngModel)]="estimate.discountLabel"
                  (ngModelChange)="onDiscountLabelChange()"
                  class="discount-label-input"
                  placeholder="Total Discount"
                  aria-label="Discount label"
                />
                <span
                  class="discount-toggle"
                  role="group"
                  aria-label="Discount mode"
                >
                  <button
                    type="button"
                    class="toggle-button"
                    [class.active]="discountMode === 'amount'"
                    (click)="setDiscountMode('amount')"
                  >
                    $
                  </button>
                  <button
                    type="button"
                    class="toggle-button"
                    [class.active]="discountMode === 'percent'"
                    (click)="setDiscountMode('percent')"
                  >
                    %
                  </button>
                </span>
              </span>
              <span>
                @if (discountMode === "amount") {
                  <input
                    type="number"
                    [(ngModel)]="estimate.totalDiscount"
                    (change)="updateTotalDiscountAmount(estimate.totalDiscount)"
                    class="table-input discount-input"
                    step="0.01"
                    min="0"
                  />
                }
                @if (discountMode === "percent") {
                  <input
                    type="number"
                    [(ngModel)]="discountPercent"
                    (change)="updateTotalDiscountPercent(discountPercent)"
                    class="table-input discount-input"
                    step="0.01"
                    min="0"
                    max="100"
                  />
                }
              </span>
            </div>
            <div class="total-row grand-total">
              <span>TOTAL</span>
              <span>{{ estimate.total | currency }}</span>
            </div>
          </div>
        </div>

        <div class="terms-section">
          <h3>Terms &amp; Notes</h3>
          <ul>
            @for (term of terms(); track $index) {
              <li>
                {{ term }}
              </li>
            }
          </ul>
        </div>

        @if (message(); as message) {
          <div class="status-message">{{ message }}</div>
        }
      </div>

      <!-- Window Selection -->
      <div class="window-selection-section">
        <h3>Include in Estimate</h3>
        @for (room of rooms(); track room.id) {
          <div class="selection-room">
            <strong>{{ room.name }}</strong>
            @for (window of windowsByRoom(room.id); track window.id) {
              <label class="selection-window">
                <input
                  type="checkbox"
                  [checked]="selectedWindowIds().includes(window.id)"
                  (change)="toggleWindow(window.id)"
                />
                {{ window.label || window.id }}
              </label>
            }
          </div>
        }
      </div>

      <!-- Supplier Panel -->
      <div class="supplier-section">
        <h2 class="supplier-title">Supplier</h2>
        <div class="supplier-assign">
          <select
            class="supplier-select"
            [(ngModel)]="selectedSupplierId"
            (change)="assignSupplier()"
          >
            <option value="">No supplier assigned</option>
            @for (supplier of suppliers(); track supplier.id) {
              <option [value]="supplier.id">{{ supplier.name }}</option>
            }
          </select>
          @if (supplierSaving()) {
            <span class="supplier-saving">Saving...</span>
          }
          <button
            class="action-button"
            (click)="copyShareLink()"
            title="Copy supplier-portal link to clipboard"
          >
            🔗 Share link
          </button>
          @if (shareCopied()) {
            <span class="supplier-saving">Link copied</span>
          }
        </div>

        @for (row of assignedSupplierRows(); track row.id) {
          <div class="supplier-quote">
            <span class="supplier-name">{{ row.name }}</span>
            @if (row.quote; as quote) {
              <a
                class="supplier-quote-link"
                [href]="quote.url"
                target="_blank"
                rel="noopener"
              >
                📎 {{ quote.name }} · {{ quote.uploadedAt | date: "MMM dd, yyyy" }}
              </a>
              @if (quote.amount != null) {
                <span class="quote-amount">Amount: ${{ quote.amount }}</span>
              }
            } @else {
              <span class="quote-amount">No quote yet</span>
            }
            <span
              class="quote-status"
              [class.quote-approved]="row.status === 'approved'"
              [class.quote-rejected]="row.status === 'rejected'"
            >
              {{ row.status }}
            </span>
            @if (row.status !== "approved" && row.status !== "rejected") {
              <div class="supplier-quote-actions">
                <button
                  class="action-button primary"
                  (click)="approveQuote(row.id)"
                  [disabled]="supplierSaving()"
                >
                  Approve
                </button>
                <button
                  class="action-button"
                  (click)="rejectQuote(row.id)"
                  [disabled]="supplierSaving()"
                >
                  Reject
                </button>
              </div>
            }
          </div>
        }
      </div>

      <!-- Private Cost Tracking Section (not included in PDF) -->
      <div class="cost-tracking-section">
        <h2 class="cost-tracking-title">
          Cost Tracking <span class="cost-tracking-badge">Private</span>
        </h2>

        <!-- Auto-calculated breakdown -->
        @if (calculatedBreakdown) {
          <div class="calc-breakdown">
            <div class="calc-breakdown-header">
              <span class="calc-breakdown-title"
                >Calculated from measurements</span
              >
              <button
                type="button"
                class="apply-calc-button"
                (click)="applyCalculatedCost()"
                title="Copy this total into the Manufacturing Cost field"
              >
                Use this total
              </button>
            </div>
            <div class="calc-breakdown-rows">
              <div class="calc-row">
                <span>Blinds</span>
                <span>{{ calculatedBreakdown.blindsCost | currency }}</span>
              </div>
              @if (calculatedBreakdown.remotes6ch > 0) {
                <div class="calc-row">
                  <span
                    >Remotes ({{ calculatedBreakdown.remotes6ch }} × 6-ch @
                    $8.90)</span
                  >
                  <span>{{
                    calculatedBreakdown.remotes6ch * 8.9 | currency
                  }}</span>
                </div>
              }
              @if (calculatedBreakdown.remotes16ch > 0) {
                <div class="calc-row">
                  <span
                    >Remotes ({{ calculatedBreakdown.remotes16ch }} × 16-ch @
                    $12.50)</span
                  >
                  <span>{{
                    calculatedBreakdown.remotes16ch * 12.5 | currency
                  }}</span>
                </div>
              }
              @if (calculatedBreakdown.solarCount > 0) {
                <div class="calc-row">
                  <span
                    >Solar panels ({{ calculatedBreakdown.solarCount }} ×
                    $21.50)</span
                  >
                  <span>{{ calculatedBreakdown.solarCost | currency }}</span>
                </div>
              }
              <div class="calc-row">
                <span
                  >Shipping ($9.85/sqm ×
                  {{ calculatedBreakdown.totalSqm | number: "1.2-2" }}
                  sqm + $300 tariff est.)</span
                >
                <span>{{ calculatedBreakdown.shippingCost | currency }}</span>
              </div>
              <div class="calc-row calc-total-row">
                <span>Estimated Total</span>
                <span>{{ calculatedBreakdown.total | currency }}</span>
              </div>
            </div>
            <p class="calc-breakdown-note">
              Blinds without a style code or measurement are excluded. Shipping
              is a base estimate only — does not include express fees or import
              tariffs. Confirm final shipping cost with Ida Blinds before
              quoting.
            </p>
          </div>
        }

        <div class="cost-tracking-grid">
          <div class="cost-field">
            <label class="cost-label">Manufacturing Cost</label>
            <div class="cost-input-wrap">
              <span class="cost-prefix">$</span>
              <input
                type="number"
                class="cost-input"
                [(ngModel)]="manufacturingCost"
                (change)="markDirty()"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        <div class="additional-costs">
          <div class="additional-costs-header">
            <span class="cost-label">Additional Costs</span>
            <button
              class="add-cost-button"
              type="button"
              (click)="addAdditionalCost()"
            >
              + Add Line
            </button>
          </div>

          @for (cost of additionalCosts; track $index) {
            <div class="additional-cost-row">
              <input
                type="text"
                class="cost-input cost-label-input"
                [(ngModel)]="cost.label"
                (change)="markDirty()"
                placeholder="e.g. Smart Hub"
              />
              <div class="cost-input-wrap">
                <span class="cost-prefix">$</span>
                <input
                  type="number"
                  class="cost-input"
                  [(ngModel)]="cost.amount"
                  (change)="markDirty()"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
              <button
                class="remove-cost-button"
                type="button"
                (click)="removeAdditionalCost($index)"
              >
                Remove
              </button>
            </div>
          }
        </div>

        <div class="cost-summary">
          <div class="cost-summary-row">
            <span>Customer Total</span>
            <span>{{ estimate.total | currency }}</span>
          </div>
          <div class="cost-summary-row">
            <span>Manufacturing Cost</span>
            <span>- {{ manufacturingCost | currency }}</span>
          </div>
          @for (cost of additionalCosts; track $index) {
            <div class="cost-summary-row">
              <span>{{ cost.label || "Additional Cost" }}</span>
              <span>- {{ cost.amount | currency }}</span>
            </div>
          }
          <div class="cost-summary-row profit-row">
            <span>Profit</span>
            <span [class.profit-negative]="profit < 0">{{
              profit | currency
            }}</span>
          </div>
        </div>
      </div>

        <div class="version-history-section">
          <h3>Version History</h3>
          @if (viewingVersion(); as v) {
            <p class="version-note">
              Viewing Revision {{ v }}.
              <button type="button" class="action-button" (click)="backToLatest()">
                Back to latest
              </button>
            </p>
          }
          @if (versions().length === 0) {
            <p class="muted">No saved versions yet.</p>
          } @else {
            <div class="table-wrap">
            <table class="version-table">
              <thead>
                <tr>
                  <th>Rev</th>
                  <th>Date</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                @for (version of versions(); track version.version) {
                  <tr [class.active]="viewingVersion() === version.version">
                    <td>v{{ version.version }}</td>
                    <td>{{ version.updatedAt | date: "MMM dd, yyyy" }}</td>
                    <td>{{ version.itemCount }}</td>
                    <td>{{ version.total | currency }}</td>
                    <td>
                      <button
                        type="button"
                        class="action-button"
                        (click)="viewVersion(version.version)"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
            </div>
          }
        </div>
    }
  </div>
</section>
